import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Flex, Loader, Progress, Text, Title } from '@mantine/core';
import { memo, ReactNode } from 'react';
import { TaskStatus } from '@dealstack/shared/lib/generated';
import type { AsyncTaskAlertCopy } from './AsyncTaskProgressDrawer.types';

interface Props {
	opened: boolean;
	onClose: () => void;
	title: ReactNode;
	// `string`, not `TaskStatus` — legacy GetBulkUploadStatusRes still types
	// status as plain string. Comparisons below work since enum values are strings.
	status?: string;
	progressPercentage?: number;
	/**
	 * While in progress and below this percentage, show an indeterminate
	 * (animated) bar instead of a tiny determinate sliver, for tasks whose
	 * first phase is one opaque step that can't report granular progress.
	 * Defaults to 0 (always determinate).
	 */
	indeterminateUntilPercentage?: number;
	/** Caption shown under the bar during the indeterminate phase. */
	preparingLabel?: ReactNode;
	inProgressAlert: AsyncTaskAlertCopy;
	doneAlert: AsyncTaskAlertCopy;
	errorAlert: AsyncTaskAlertCopy;
	/** Extra content rendered below the progress bar while the task is running. */
	inProgressBody?: ReactNode;
	/** Body content rendered when status === DONE (replaces the progress bar). */
	doneBody?: ReactNode;
	/** Body content rendered when status === ERROR. */
	errorBody?: ReactNode;
	closeLabel: string;
}

const AsyncTaskProgressDrawer = ({
	opened,
	onClose,
	title,
	status,
	progressPercentage,
	indeterminateUntilPercentage = 0,
	preparingLabel,
	inProgressAlert,
	doneAlert,
	errorAlert,
	inProgressBody,
	doneBody,
	errorBody,
	closeLabel,
}: Props) => {
	const isInProgress = status === TaskStatus.PENDING || status === TaskStatus.IN_PROGRESS;
	const isDone = status === TaskStatus.DONE;
	const isError = status === TaskStatus.ERROR || status === TaskStatus.CANCELLED;
	const isPreparing = isInProgress && (progressPercentage ?? 0) < indeterminateUntilPercentage;

	let currentAlert: AsyncTaskAlertCopy;
	if (isError) {
		currentAlert = errorAlert;
	} else if (isDone) {
		currentAlert = doneAlert;
	} else {
		currentAlert = inProgressAlert;
	}

	const progressBar = isPreparing ? (
		<Flex direction="column" align="center" mt={6} gap={8} w="100%">
			<Progress value={100} size="xl" p={4} h={24} w="100%" striped animated />
			{preparingLabel && (
				<Text size="xs" c="dimmed">
					{preparingLabel}
				</Text>
			)}
		</Flex>
	) : (
		<Flex justify="center" align="center" mt={6}>
			{progressPercentage === 0 && (
				<Loader size="1.5rem" style={{ position: 'absolute', zIndex: 2 }} />
			)}
			<Progress
				value={progressPercentage ?? 0}
				size="xl"
				p={4}
				h={24}
				w="100%"
				styles={{
					root: { opacity: progressPercentage === 0 ? 0.3 : 1 },
					bar: { transition: 'width 1s ease-in-out' },
				}}
			/>
		</Flex>
	);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				<Title order={4} fw={600}>
					{title}
				</Title>
			}
		>
			<Flex direction="column" justify="space-between" h="100%">
				<Flex direction="column">
					<InfoBox
						title={currentAlert.title}
						description={currentAlert.description}
						iconVariant={isError ? 'warning' : 'information'}
						rippleColor={isError ? 'red' : 'blue'}
					/>

					{isInProgress && (
						<Flex direction="column" mt={20}>
							{inProgressBody}
							{progressBar}
						</Flex>
					)}
					{isDone && doneBody && (
						<Flex direction="column" mt={20}>
							{doneBody}
						</Flex>
					)}
					{isError && errorBody && (
						<Flex direction="column" mt={20}>
							{errorBody}
						</Flex>
					)}
				</Flex>

				<Button disabled={isInProgress} onClick={onClose} fullWidth>
					{closeLabel}
				</Button>
			</Flex>
		</Drawer>
	);
};

const _AsyncTaskProgressDrawerExport = memo(AsyncTaskProgressDrawer);
export { _AsyncTaskProgressDrawerExport as AsyncTaskProgressDrawer };
